import { ApplicationFormValues } from './applicationValidation';
import { StepId } from '@/components/hook/applicationSteps';

/** Joins the parts that have a value, so a half-filled step never shows " · · ". */
function join(parts: (string | undefined | null)[], separator = ' · ') {
  return parts
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(separator);
}

/**
 * The recap of a collapsed step, as one line per entry.
 *
 * "My Info" is a list — the design stacks name, birthday, email, phone and the
 * handles on their own lines, which is also the only way it stays readable at
 * the 16px the design sets. The address and payment method are single lines.
 *
 * Returns an empty array when there is nothing worth showing, which is the
 * signal not to render the recap at all — a section the applicant has not
 * reached yet shows its title alone, not an empty caption.
 */
export function summaryForStep(
  step: StepId,
  values: ApplicationFormValues,
  /** The already-formatted payment method label, or null while unsettled. */
  paymentMethod: string | null = null
): string[] {
  if (step === 'info') {
    return [
      join([values.firstName, values.lastName], ' '),
      values.birthday,
      values.email,
      values.phone,
      values.instagram,
      values.tiktok
    ]
      .map((line) => line?.trim())
      .filter((line): line is string => !!line);
  }

  if (step === 'shipping') {
    const street = join([values.address, values.aptUnitSuite], ', ');
    const region = join([values.city, values.state], ', ');
    const address = join([street, region, values.postalCode], ', ');

    return address ? [address] : [];
  }

  return paymentMethod ? [paymentMethod] : [];
}
